import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { Close as CloseIcon, History as HistoryIcon } from '@mui/icons-material';
import { Party } from '../types';
import RevisionWorkflowForm from './forms/RevisionWorkflowForm';
import {
  RevisionWorkflowFormValues,
  buildDefaultRevisionWorkflowFormValues,
  getRevisionWorkflowFormMissingFields,
  labelsForDocumentFields,
} from './forms/formTypes';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import FormValidationAlert from './FormValidationAlert';
import { cloneRevisionWorkflowForm } from './forms/workflowUtils';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };

export interface WorkflowDialogProps {
  open: boolean;
  onClose: () => void;
  documentTitle: string;
  revisionLabel?: string;
  parties: Party[];
  initialWorkflow?: RevisionWorkflowFormValues | null;
  recreateWorkflow?: RevisionWorkflowFormValues | null;
  recreateWorkflowSource?: string;
  onSubmit: (workflow: RevisionWorkflowFormValues) => void | Promise<void>;
  loading?: boolean;
}

const WorkflowDialog: React.FC<WorkflowDialogProps> = ({
  open,
  onClose,
  documentTitle,
  revisionLabel,
  parties,
  initialWorkflow = null,
  recreateWorkflow = null,
  recreateWorkflowSource,
  onSubmit,
  loading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';
  const inkSecondary = isDark ? '#b0bec5' : '#546e7a';

  const [workflowForm, setWorkflowForm] = useState<RevisionWorkflowFormValues>(
    buildDefaultRevisionWorkflowFormValues()
  );
  const [recreateApplied, setRecreateApplied] = useState(false);
  const fieldErrors = useFormFieldErrors();

  useEffect(() => {
    if (open) {
      setWorkflowForm({
        ...buildDefaultRevisionWorkflowFormValues(),
        ...initialWorkflow,
        checkers: initialWorkflow?.checkers ? [...initialWorkflow.checkers] : [],
        approvers: initialWorkflow?.approvers ? [...initialWorkflow.approvers] : [],
      });
      setRecreateApplied(false);
      fieldErrors.clear();
    }
  }, [open, initialWorkflow]);

  const canRecreateLastWorkflow = Boolean(recreateWorkflow && recreateWorkflowSource);

  const handleRecreateLastWorkflow = () => {
    if (!recreateWorkflow) {
      return;
    }
    setWorkflowForm(cloneRevisionWorkflowForm(recreateWorkflow));
    setRecreateApplied(true);
  };

  const handleSubmit = () => {
    const missing = getRevisionWorkflowFormMissingFields(workflowForm);
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForDocumentFields(missing));
      return;
    }
    fieldErrors.clear();
    void onSubmit(workflowForm);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: surfaceBg,
          border: `1px solid ${panelBorder}`,
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5, pr: 1.5 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box minWidth={0} flex={1}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              }}
            >
              Edit Revision Workflow
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: inkSecondary,
                fontFamily: 'monospace',
              }}
            >
              {documentTitle}
              {revisionLabel ? ` · ${revisionLabel}` : ''}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            {canRecreateLastWorkflow && (
              <Tooltip
                title={
                  recreateApplied
                    ? `Workflow from ${recreateWorkflowSource} applied — save to confirm`
                    : `Copy originator, checkers, and approvers from revision ${recreateWorkflowSource}`
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<HistoryIcon sx={{ fontSize: '1rem !important' }} />}
                    onClick={handleRecreateLastWorkflow}
                    disabled={loading}
                    sx={{
                      minHeight: 32,
                      px: 1.25,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: recreateApplied ? NAUTILUS.deepOcean : inkSecondary,
                      borderColor: recreateApplied
                        ? NAUTILUS.sand
                        : panelBorder,
                      bgcolor: recreateApplied
                        ? alpha(NAUTILUS.sand, isDark ? 0.18 : 0.22)
                        : isDark
                          ? alpha(NAUTILUS.abyssal, 0.35)
                          : alpha(NAUTILUS.deepOcean, 0.03),
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderColor: NAUTILUS.sand,
                        bgcolor: alpha(NAUTILUS.sand, isDark ? 0.12 : 0.14),
                        color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
                      },
                      '&.Mui-disabled': {
                        borderColor: panelBorder,
                        color: alpha(inkSecondary, 0.5),
                      },
                    }}
                  >
                    Use Last Workflow
                  </Button>
                </span>
              </Tooltip>
            )}
            <IconButton onClick={onClose} size="small" sx={{ color: inkSecondary }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />
      <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
        <RevisionWorkflowForm
          value={workflowForm}
          onChange={setWorkflowForm}
          parties={parties}
          invalid={fieldErrors.invalid}
          onFieldEdit={fieldErrors.clearField}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            bgcolor: NAUTILUS.sand,
            color: NAUTILUS.deepOcean,
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' },
            '&:disabled': { bgcolor: alpha(NAUTILUS.sand, 0.3) },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: NAUTILUS.deepOcean }} />
          ) : (
            'Save Workflow'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowDialog;
